import { AlertCircle } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";

// The decision panel for a FAILED item — file name, the server's own reason,
// then the three actions (Retry / Fill in manually / Abort) rendered exactly
// from the booleans the server sent, since those fold in rules invisible from
// here (batch state, the retry cap, whether the failure happened during
// extraction or at final submit).
const FailedItemPanel = ({ item, maxRetries, retrying, manualLoading, aborting, error, onRetry, onManualEntry, onAbortRequest }) => {
  const noActionsAtAll = !item.can_retry && !item.can_manual_entry && !item.can_abort;
  const retriesSpent = !item.can_retry && !noActionsAtAll;
  const retryLabel = item.retries_remaining === maxRetries ? "Retry" : `Retry (${item.retries_remaining} left)`;
  const anyActionInFlight = retrying || manualLoading || aborting;

  return (
    <Card className="h-full overflow-y-auto custom-scrollbar flex flex-col items-start justify-start p-6 gap-4">
      <div className="flex items-center gap-2 w-full">
        <AlertCircle className="h-5 w-5 shrink-0 text-tone-rose" />
        <h3 className="text-sm font-medium text-text-primary truncate">{item.file_name || "This resume"}</h3>
      </div>

      <p className="text-sm text-text-secondary">{item.extraction_error || "We couldn't extract information from this resume."}</p>
      <p className="text-xs text-text-secondary">Please check that you've uploaded the correct document and try again.</p>

      {error && <p className="text-[11px] text-tone-rose">{error}</p>}

      {!noActionsAtAll && (
        <div className="flex flex-col gap-2 w-full mt-2">
          <div className="flex flex-wrap items-center gap-2">
            {item.can_retry && (
              <Button onClick={onRetry} disabled={anyActionInFlight} className="text-xs bg-button-secondary text-text-base rounded-lg px-4 py-2 disabled:opacity-50">
                {retrying ? "Retrying…" : retryLabel}
              </Button>
            )}
            {item.can_manual_entry && (
              <Button
                onClick={onManualEntry}
                disabled={anyActionInFlight}
                className="text-xs border border-border-primary text-text-primary rounded-lg px-4 py-2 disabled:opacity-50"
              >
                {manualLoading ? "Starting…" : "Fill in manually"}
              </Button>
            )}
            {item.can_abort && (
              <Button
                onClick={onAbortRequest}
                disabled={anyActionInFlight}
                className="text-xs text-tone-rose border border-tone-rose rounded-lg px-4 py-2 disabled:opacity-50"
              >
                {aborting ? "Removing…" : "Remove"}
              </Button>
            )}
          </div>
          {retriesSpent && <p className="text-[11px] text-tone-amber">No retries left — fill it in manually, or remove it.</p>}
        </div>
      )}
    </Card>
  );
};

export default FailedItemPanel;
